"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.projectDiffSchema = void 0;
exports.generateId = generateId;
exports.applyDiffToOutline = applyDiffToOutline;
exports.computeDiffHighlighting = computeDiffHighlighting;
const zod_1 = require("zod");
const project_1 = require("./project");
function generateId() {
    return crypto.randomUUID();
}
const createWorkstreamChangeSchema = zod_1.z.object({
    type: zod_1.z.literal("createWorkstream"),
    name: zod_1.z.string(),
    description: zod_1.z.string(),
    deliverables: zod_1.z.array(project_1.workstreamDeliverableSchema),
});
const createDeliverableChangeSchema = zod_1.z.object({
    type: zod_1.z.literal("createDeliverable"),
    workstreamId: zod_1.z.string(),
    name: zod_1.z.string(),
    icon: zod_1.z.string(),
    description: zod_1.z.string(),
    startDate: zod_1.z.string().date().optional(),
    targetDate: zod_1.z.string().date().optional(),
});
const deleteWorkstreamChangeSchema = zod_1.z.object({
    type: zod_1.z.literal("deleteWorkstream"),
    workstreamId: zod_1.z.string(),
});
const deleteDeliverableChangeSchema = zod_1.z.object({
    type: zod_1.z.literal("deleteDeliverable"),
    workstreamId: zod_1.z.string(),
    deliverableId: zod_1.z.string(),
});
const updateWorkstreamChangeSchema = zod_1.z.object({
    type: zod_1.z.literal("updateWorkstream"),
    workstreamId: zod_1.z.string(),
    name: zod_1.z.string().optional(),
    description: zod_1.z.string().optional(),
});
const updateDeliverableChangeSchema = zod_1.z.object({
    type: zod_1.z.literal("updateDeliverable"),
    workstreamId: zod_1.z.string(),
    deliverableId: zod_1.z.string(),
    name: zod_1.z.string().optional(),
    icon: zod_1.z.string().optional(),
    description: zod_1.z.string().optional(),
});
exports.projectDiffSchema = zod_1.z.object({
    id: zod_1.z.string(),
    changes: zod_1.z.array(zod_1.z.discriminatedUnion("type", [
        createWorkstreamChangeSchema,
        updateWorkstreamChangeSchema,
        deleteWorkstreamChangeSchema,
        createDeliverableChangeSchema,
        updateDeliverableChangeSchema,
        deleteDeliverableChangeSchema,
    ])),
});
function applyDiffToOutline(outline, diff) {
    const newOutline = JSON.parse(JSON.stringify(outline));
    for (const change of diff.changes) {
        switch (change.type) {
            case "createWorkstream": {
                const newWorkstream = {
                    id: generateId(),
                    name: change.name,
                    description: change.description,
                    deliverables: change.deliverables.map((d) => ({
                        ...d,
                        id: generateId(),
                    })),
                };
                newOutline.workstreams.push(newWorkstream);
                break;
            }
            case "updateWorkstream": {
                const workstream = newOutline.workstreams.find((w) => w.id === change.workstreamId);
                if (workstream) {
                    if (change.name !== undefined) {
                        workstream.name = change.name;
                    }
                    if (change.description !== undefined) {
                        workstream.description = change.description;
                    }
                }
                break;
            }
            case "deleteWorkstream": {
                const index = newOutline.workstreams.findIndex((w) => w.id === change.workstreamId);
                if (index !== -1) {
                    newOutline.workstreams.splice(index, 1);
                }
                break;
            }
            case "createDeliverable": {
                const workstream = newOutline.workstreams.find((w) => w.id === change.workstreamId);
                if (workstream) {
                    const newDeliverable = {
                        id: generateId(),
                        name: change.name,
                        icon: change.icon,
                        description: change.description,
                        startDate: change.startDate,
                        targetDate: change.targetDate,
                    };
                    workstream.deliverables.push(newDeliverable);
                }
                break;
            }
            case "updateDeliverable": {
                const workstream = newOutline.workstreams.find((w) => w.id === change.workstreamId);
                if (workstream) {
                    const deliverable = workstream.deliverables.find((d) => d.id === change.deliverableId);
                    if (deliverable) {
                        if (change.name !== undefined) {
                            deliverable.name = change.name;
                        }
                        if (change.icon !== undefined) {
                            deliverable.icon = change.icon;
                        }
                        if (change.description !== undefined) {
                            deliverable.description = change.description;
                        }
                    }
                }
                break;
            }
            case "deleteDeliverable": {
                const workstream = newOutline.workstreams.find((w) => w.id === change.workstreamId);
                if (workstream) {
                    const index = workstream.deliverables.findIndex((d) => d.id === change.deliverableId);
                    if (index !== -1) {
                        workstream.deliverables.splice(index, 1);
                    }
                }
                break;
            }
        }
    }
    return newOutline;
}
function computeDiffHighlighting(original, diff) {
    const res = {
        workstreams: {},
    };
    const originalIds = new Set(original.workstreams.map((w) => w.id));
    const result = applyDiffToOutline(original, diff);
    const resultIds = new Set(result.workstreams.map((w) => w.id));
    for (const id of resultIds) {
        if (!originalIds.has(id)) {
            res.workstreams[id] = "created";
        }
    }
    for (const id of originalIds) {
        if (!resultIds.has(id)) {
            res.workstreams[id] = "deleted";
        }
    }
    for (const change of diff.changes) {
        switch (change.type) {
            case "updateWorkstream":
                res.workstreams[change.workstreamId] = "updated";
                break;
            case "createDeliverable":
            case "updateDeliverable":
            case "deleteDeliverable":
                if (!res.workstreams[change.workstreamId]) {
                    res.workstreams[change.workstreamId] = "updated";
                }
                break;
        }
    }
    return res;
}
